import { useTranslation } from 'next-i18next';
import React, {
    useContext, useEffect, useState, useRef,
} from 'react';
import { HeaderContext } from '@contexts/HeaderContext';
import { useSiteInfo } from '@contexts/SiteInfoContext';
import SiteMapping from '@configs/siteMapping';
import RightNav from '@components/RightNav';
import { pushClickHeaderNavEventLog, pushClickLoginButtonEventLog } from '../../utils/GrafanaFaro/pushEventLog';
import DropdownButton from './DropDownContainer';
import { joinNow } from '../../utils/login';
import { getNameByCode } from '../common/Icon';
import { platformList } from '../../utils/dropdownList';
import HamburgerBtn from '../RightNav/HamburgerBtn';
import ErrorBoundary from '../common/ErrorBoundary';
import ServerPath from '../../configs/serverPath';
import headerLayout from '../../utils/headerLayout';
import useCurrentPlatformUMStatus from '../../hooks/useCurrentPlatformUMStatus';
import { getDomainName } from '../../utils/domainUtils';
import { canNotSeeNovaCasinoDomainList } from '../Footer/constants';
import { DEVICE_TYPE, getUserDevice } from '../../utils/getUserDevice';
import { COUNTRY_CODE } from '../../utils/countryCode.constant';
import SportsGameCountdown from './SportsGameCountdown';
import { LiveChatButton } from '@components/Features/CSDChat';
import CasinoButton from '@components/Features/CasinoButton';

const sportList = (siteName, composePath) => {
    const subPath = 'products';
    const withoutSabaGameSite = [SiteMapping.Nova88, SiteMapping.IBCBet, SiteMapping.bettha88, SiteMapping.taraPlays,
        SiteMapping.fifabet, SiteMapping.ocean99];
    const withoutOneSealSite = [SiteMapping.PICA88];

    const list = [{
        sportName: 'lbl_sports',
        imageUrl: composePath({ pathType: ServerPath.siteImage, pathName: `${subPath}/Sports_desktop.png` }),
    },
    {
        sportName: 'lbl_VirtualSports',
        imageUrl: composePath({ pathType: ServerPath.siteImage, pathName: `${subPath}/VirtualSports_desktop.png` }),
    },
    {
        sportName: 'lbl_Esports',
        imageUrl: composePath({ pathType: ServerPath.siteImage, pathName: `${subPath}/E-Sports_desktop.png` }),
    },
    {
        sportName: 'lbl_NumberGame',
        imageUrl: composePath({ pathType: ServerPath.siteImage, pathName: `${subPath}/NumberGame_desktop.png` }),
    },
    {
        sportName: 'lbl_Gaming',
        imageUrl: composePath({ pathType: ServerPath.siteImage, pathName: `${subPath}/Gaming_desktop.png` }),
    },
    {
        sportName: 'lbl_RNGKeno',
        imageUrl: composePath({ pathType: ServerPath.siteImage, pathName: `${subPath}/RNGKeno_desktop.png` }),
    },

    {
        sportName: 'lbl_Sabagames',
        imageUrl: composePath({ pathType: ServerPath.siteImage, pathName: `${subPath}/Saba.club_desktop.png` }),
    },
    ];
    let newList = [...list];
    if (withoutSabaGameSite.includes(siteName)) {
        newList.pop();
    }
    if (siteName === SiteMapping.taraPlays) {
        // taraPlays switch sabaGame to vgaming
        newList.push({
            sportName: 'Vgaming',
            imageUrl: composePath({ pathType: ServerPath.siteImage, pathName: `${subPath}/Vgaming_desktop` }),
        });
    }
    if (withoutOneSealSite.includes(siteName)) {
        newList = list.filter((item) => {
            return ['lbl_sports', 'lbl_Esports'].includes(item.sportName);
        });
    }
    return newList;
};

const SportListContainer = ({ siteName, children }) => {
    return (
        {
            [SiteMapping.Nova88]: children,
            [SiteMapping.IBCBet]: children,
            [SiteMapping.PICA88]: children,
            [SiteMapping.id8899]: children,
            [SiteMapping.viva88]: <div className="header-nav__main">{children}</div>,
            [SiteMapping.bong88]: <div className="header-nav__main">{children}</div>,
            [SiteMapping.bettha88]: children,
            [SiteMapping.taraPlays]: children,
            [SiteMapping.fifabet]: children,
            [SiteMapping.ocean99]: children,
        }[siteName]
    );
};

const ButtonClassName = {
    [SiteMapping.Nova88]: { LOGIN_POPUP: 'btn btn--secondary', JOIN_NOW: 'btn btn--primary' },
    [SiteMapping.PICA88]: { LOGIN_POPUP: 'btn btn--secondary', JOIN_NOW: 'btn btn--primary' },
    [SiteMapping.viva88]: { LOGIN_POPUP: 'btn btn--secondary', JOIN_NOW: 'btn btn--primary' },
    [SiteMapping.bong88]: { LOGIN_POPUP: 'btn btn--secondary', JOIN_NOW: 'btn btn--primary' },
    [SiteMapping.IBCBet]: { LOGIN_POPUP: 'btn btn--secondary', JOIN_NOW: 'btn btn--primary' },
    [SiteMapping.id8899]: { LOGIN_POPUP: 'btn btn--secondary', JOIN_NOW: 'btn btn--primary' },
    [SiteMapping.bettha88]: { LOGIN_POPUP: 'btn btn--secondary', JOIN_NOW: 'btn btn--primary' },
    [SiteMapping.taraPlays]: { LOGIN_POPUP: 'btn btn--secondary', JOIN_NOW: 'btn btn--primary' },
    [SiteMapping.fifabet]: { LOGIN_POPUP: 'btn btn--secondary', JOIN_NOW: 'btn btn--primary' },
    [SiteMapping.ocean99]: { LOGIN_POPUP: 'btn btn--secondary', JOIN_NOW: 'btn btn--primary' },
};

const HeaderButtonsContainer = ({ siteName, children }) => {
    return (
        {
            [SiteMapping.Nova88]: <div className="header-login">{children}</div>,
            [SiteMapping.IBCBet]: <div className="header-login">{children}</div>,
            [SiteMapping.PICA88]: <div className="header-login">{children}</div>,
            [SiteMapping.id8899]: <div className="header-login">{children}</div>,
            [SiteMapping.viva88]: children,
            [SiteMapping.bong88]: children,
            [SiteMapping.bettha88]: <div className="header-login">{children}</div>,
            [SiteMapping.taraPlays]: <div className="header-login">{children}</div>,
            [SiteMapping.fifabet]: <div className="header-login">{children}</div>,
            [SiteMapping.ocean99]: <div className="header-login">{children}</div>,
        }[siteName]
    );
};

const novaCasinoUrl = 'https://www.novacasino.vip/login';
const novaCasinoGtag = 'click_outbound2novacasino';
const novaJoinNowGtag = 'before_JoinUs';

const HeaderButtons = ({ siteName, platformState }) => {
    const { t } = useTranslation();
    const [state, dispatch] = useContext(HeaderContext);
    const {
        countryCode, setIsShowJoinNowButton, isShowJoinNowButton,
        landingPageUrl,
    } = useSiteInfo();
    const [showDesktopVersionText, setShowDesktopVersionText] = useState(false);
    const excludeJoinNowBtnDomainList = [SiteMapping.PICA88, SiteMapping.id8899, SiteMapping.taraPlays, SiteMapping.bettha88,
        SiteMapping.fifabet, SiteMapping.ocean99, SiteMapping.IBCBet];
    const [isShowEntryButton, setIsShowEntryButton] = useState(false);
    const [gtagState, setGtagState] = useState('');
    const { fetchCurrentPlatUMData } = useCurrentPlatformUMStatus();

    const getCanSeeNovaCasino = () => {
        const device = getUserDevice();
        const isMobile = device === DEVICE_TYPE.MOBILE;
        const enableCountryCode = countryCode === COUNTRY_CODE.MY;
        const canSeeNovaCasino = siteName === SiteMapping.Nova88 && !canNotSeeNovaCasinoDomainList.includes(getDomainName()) && isMobile && enableCountryCode;
        return canSeeNovaCasino;
    };

    const handleShowEntryButton = () => {
        setIsShowJoinNowButton(
            !excludeJoinNowBtnDomainList.includes(siteName),
        );
        setGtagState(novaJoinNowGtag);
    };

    useEffect(() => {
        if (state.platform === 'desktop') {
            setShowDesktopVersionText(true);
        } else {
            setShowDesktopVersionText(false);
        }
    }, [state.platform]);

    const joinNowUrl = landingPageUrl;

    const buttonText = t('lbl_Join_Now');

    const handleLogin = async () => {
        const isCurrentPlatformUM = await fetchCurrentPlatUMData();
        if (isCurrentPlatformUM) {
            dispatch({ type: 'SHOW_UM_POPUP' });
        } else {
            dispatch({ type: 'SHOW_LOGIN_POPUP' });
        }
    };

    useEffect(() => {
        handleShowEntryButton();
    }, []);

    return (
        <>
            {!headerLayout.layout789y.includes(siteName) && showDesktopVersionText && (
                <span
                    className="text"
                    data-value={t('lbl_login_desktop')}
                    role="mobile-visible"
                />
            )}
            {
                isShowJoinNowButton && joinNowUrl && (
                    <a
                        className={ButtonClassName[siteName].JOIN_NOW}
                        onClick={() => joinNow(siteName, state.language, joinNowUrl)}
                        gtag={gtagState}
                    >
                        {headerLayout.layoutNova88.includes(siteName) ? (
                            <>
                                <i className="icon icon--add-account" role="mobile-visible"></i>
                                <span className="text" role="desktop-visible">
                                    {buttonText}
                                </span>
                            </>
                        ) : (
                            <span className="text">{buttonText}</span>
                        )}
                    </a>
                )
            }
            <a
                className={ButtonClassName[siteName].LOGIN_POPUP}
                onClick={handleLogin}
            >
                {headerLayout.layoutNova88.includes(siteName) ? (
                    <>
                        <i className="icon icon--login" role="mobile-visible"></i>
                        <span className="text" role="desktop-visible">{t('lbl_login')}</span>
                    </>
                ) : (
                    <span className="text">{t('lbl_login')}</span>
                )}
            </a>
            {headerLayout.layout789y.includes(siteName) && showDesktopVersionText && (
                <span
                    className="text"
                    data-value={t('lbl_login_desktop')}
                    role="mobile-visible"
                />
            )}
        </>
    );
};

const SponsorLogo = () => {
    const { composePath } = useSiteInfo();
    return (
        <img
            className="header-sponsor-logo"
            src={composePath({
                pathType: ServerPath.siteImage,
                pathName: 'logo_sponsor/logo_sponsor.png',
            })}
        />
    );
};

const Header = () => {
    const [state, dispatch] = useContext(HeaderContext);
    const { siteName, composePath, enableSponsorLogo, enableFestivalLogo, sealCasinoPortalUrl } = useSiteInfo();
    const [isHover, setIsHover] = useState(false);
    const { t } = useTranslation();
    const [platformState, setPlatformState] = useState(state.platform);
    const { fetchCurrentPlatUMData } = useCurrentPlatformUMStatus();
    const [festivalVideoExists, setFestivalVideoExists] = useState(true);

    useEffect(() => {
        setPlatformState(getNameByCode(platformList, state.platform));
    }, [state.platform]);

    const displayHeaderProduct = (siteName) => {
        return {
            [SiteMapping.viva88]: true,
            [SiteMapping.bong88]: true,
            [SiteMapping.IBCBet]: false,
            [SiteMapping.PICA88]: false,
            [SiteMapping.Nova88]: false,
            [SiteMapping.id8899]: false,
            [SiteMapping.bettha88]: false,
            [SiteMapping.taraPlays]: false,
            [SiteMapping.fifabet]: false,
            [SiteMapping.ocean99]: false,
        }[siteName];
    };

    // Right Nav
    const closeButtonRef = useRef(null);

    const handleLogin = async () => {
        setIsHover(false);
        const isCurrentPlatformUM = await fetchCurrentPlatUMData();
        if (isCurrentPlatformUM) {
            dispatch({ type: 'SHOW_UM_POPUP' });
        } else {
            dispatch({ type: 'SHOW_LOGIN_POPUP' });
        }
    };

    return (
        <ErrorBoundary>
            <div className="header">
                <div className="header__main">
                    <div className="header-logo">
                        {enableFestivalLogo && festivalVideoExists && (
                            <img className="header-festival-decoration" src={composePath({ pathType: ServerPath.globalImage, pathName: 'logo_decoration/logo-decoration.gif' })} alt="festival-logo-decoration" onError={() => setFestivalVideoExists(false)} />
                        )}
                        {SiteMapping.Nova88 === siteName && enableSponsorLogo ? <SponsorLogo /> : null}
                    </div>

                    <div
                        role={headerLayout.layout789y.includes(siteName) ? 'desktop-visible' : ''}
                        className={`header-nav ${isHover ? 'is-hover' : ''}`}
                        onMouseEnter={() => setIsHover(true)}
                        onMouseLeave={() => setIsHover(false)}
                        onClick={() => {
                            handleLogin();
                        }}
                    >
                        <SportListContainer siteName={siteName}>
                            {sportList(siteName, composePath).map(({ sportName }) => (
                                <div key={sportName} className="header-nav__item">
                                    <a className="btn">
                                        <span className="text">{t(sportName)}</span>
                                    </a>
                                </div>
                            ))}
                        </SportListContainer>
                        {/* hover 卡片 */}
                        {displayHeaderProduct(siteName) && (
                            <div
                                className={`header-nav__product ${isHover ? 'is-hover' : ''}`}
                            >
                                <div className="sports-list">
                                    {
                                        sportList(siteName, composePath).map(({ sportName, imageUrl }) => (
                                            <a key={sportName} className="sports-card">
                                                <div className="sports-card__title">
                                                    <span className="text">{t(sportName)}</span>
                                                </div>
                                                <div className="sports-card__image">
                                                    <img src={`${imageUrl}`} alt="" />
                                                </div>
                                            </a>
                                        ))
                                    }
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="header-option">
                        <DropdownButton
                            selectIconName={`icon icon--${state.platform}`}
                            dropdownType="platform"
                            layoutType="header"
                        />
                        <DropdownButton
                            selectIconName={`flag flag--${state.language}`}
                            dropdownType="language"
                            layoutType="header"
                        />
                        {[SiteMapping.Nova88, SiteMapping.fifabet, SiteMapping.ocean99].includes(siteName) &&
                            <div className="header-option__btn" role="desktop-visible">
                                <LiveChatButton siteName={siteName} />
                                <span className="text">{t('lbl_LiveChat')}</span>
                            </div>
                        }
                        {headerLayout.layoutSupportCasino.includes(siteName) && sealCasinoPortalUrl &&
                            <div className="header-option__btn">
                                <CasinoButton />
                                <span className="text" role="desktop-visible">{t('lbl_ibc_casino')}</span>
                            </div>
                        }
                        {/* viva88: HeaderButtonsContainer */}
                        {
                            {
                                [SiteMapping.viva88]: (
                                    <HeaderButtonsContainer siteName={siteName} platformState={platformState}>
                                        <HeaderButtons siteName={siteName} platformState={platformState} />
                                    </HeaderButtonsContainer>
                                ),
                                [SiteMapping.bong88]: (
                                    <HeaderButtonsContainer siteName={siteName} platformState={platformState}>
                                        <HeaderButtons siteName={siteName} platformState={platformState} />
                                    </HeaderButtonsContainer>
                                ),

                            }[siteName]
                        }

                    </div>
                    {/* nova88: HeaderButtonsContainer */}
                    {
                        {
                            [SiteMapping.Nova88]: (
                                <HeaderButtonsContainer siteName={siteName} platformState={platformState}>
                                    <HeaderButtons siteName={siteName} platformState={platformState} />
                                </HeaderButtonsContainer>
                            ),
                            [SiteMapping.IBCBet]: (
                                <HeaderButtonsContainer siteName={siteName} platformState={platformState}>
                                    <HeaderButtons siteName={siteName} platformState={platformState} />
                                </HeaderButtonsContainer>
                            ),
                            [SiteMapping.PICA88]: (
                                <HeaderButtonsContainer siteName={siteName} platformState={platformState}>
                                    <HeaderButtons siteName={siteName} platformState={platformState} />
                                </HeaderButtonsContainer>
                            ),
                            [SiteMapping.id8899]: (
                                <HeaderButtonsContainer siteName={siteName} platformState={platformState}>
                                    <HeaderButtons siteName={siteName} platformState={platformState} />
                                </HeaderButtonsContainer>
                            ),
                            [SiteMapping.bettha88]: (
                                <HeaderButtonsContainer siteName={siteName} platformState={platformState}>
                                    <HeaderButtons siteName={siteName} platformState={platformState} />
                                </HeaderButtonsContainer>
                            ),
                            [SiteMapping.taraPlays]: (
                                <HeaderButtonsContainer siteName={siteName} platformState={platformState}>
                                    <HeaderButtons siteName={siteName} platformState={platformState} />
                                </HeaderButtonsContainer>
                            ),
                            [SiteMapping.fifabet]: (
                                <HeaderButtonsContainer siteName={siteName} platformState={platformState}>
                                    <HeaderButtons siteName={siteName} platformState={platformState} />
                                </HeaderButtonsContainer>
                            ),
                            [SiteMapping.ocean99]: (
                                <HeaderButtonsContainer siteName={siteName} platformState={platformState}>
                                    <HeaderButtons siteName={siteName} platformState={platformState} />
                                </HeaderButtonsContainer>
                            ),
                        }[siteName]
                    }

                </div>

            </div>
            {/*  RightNav 內容 */}
            {
                {
                    [SiteMapping.Nova88]: (
                        <RightNav
                            siteName={siteName}
                            closeButtonRef={closeButtonRef}
                            liveChat={true}
                        >
                            <HamburgerBtn
                                siteName={siteName}
                                closeButtonRef={closeButtonRef}
                                liveChat={true}
                            />
                        </RightNav>
                    ),
                    [SiteMapping.IBCBet]: (
                        <RightNav
                            siteName={siteName}
                            closeButtonRef={closeButtonRef}
                        >
                            <HamburgerBtn
                                siteName={siteName}
                                closeButtonRef={closeButtonRef}
                            />
                        </RightNav>
                    ),
                    [SiteMapping.PICA88]: (
                        <RightNav
                            siteName={siteName}
                            closeButtonRef={closeButtonRef}
                        >
                            <HamburgerBtn
                                siteName={siteName}
                                closeButtonRef={closeButtonRef}
                            />
                        </RightNav>
                    ),
                    [SiteMapping.id8899]: (
                        <RightNav
                            siteName={siteName}
                            closeButtonRef={closeButtonRef}
                        >
                            <HamburgerBtn
                                siteName={siteName}
                                closeButtonRef={closeButtonRef}
                            />
                        </RightNav>
                    ),
                    [SiteMapping.viva88]: (
                        <RightNav
                            siteName={siteName}
                            closeButtonRef={closeButtonRef}
                        >
                            <HamburgerBtn
                                siteName={siteName}
                                closeButtonRef={closeButtonRef}
                            />
                        </RightNav>
                    ),
                    [SiteMapping.bong88]: (
                        <RightNav
                            siteName={siteName}
                            closeButtonRef={closeButtonRef}
                        >
                            <HamburgerBtn
                                siteName={siteName}
                                closeButtonRef={closeButtonRef}
                            />
                        </RightNav>
                    ),
                    [SiteMapping.bettha88]: (
                        <RightNav
                            siteName={siteName}
                            closeButtonRef={closeButtonRef}
                        >
                            <HamburgerBtn
                                siteName={siteName}
                                closeButtonRef={closeButtonRef}
                            />
                        </RightNav>
                    ),
                    [SiteMapping.taraPlays]: (
                        <RightNav
                            siteName={siteName}
                            closeButtonRef={closeButtonRef}
                        >
                            <HamburgerBtn
                                siteName={siteName}
                                closeButtonRef={closeButtonRef}
                            />
                        </RightNav>
                    ),
                    [SiteMapping.fifabet]: (
                        <RightNav
                            siteName={siteName}
                            closeButtonRef={closeButtonRef}
                            liveChat={true}
                        >
                            <HamburgerBtn
                                siteName={siteName}
                                closeButtonRef={closeButtonRef}
                                liveChat={true}
                            />
                        </RightNav>
                    ),
                    [SiteMapping.ocean99]: (
                        <RightNav
                            siteName={siteName}
                            closeButtonRef={closeButtonRef}
                            liveChat={true}
                        >
                            <HamburgerBtn
                                siteName={siteName}
                                closeButtonRef={closeButtonRef}
                                liveChat={true}
                            />
                        </RightNav>
                    ),
                }[siteName]
            }

            <SportsGameCountdown />
        </ErrorBoundary>
    );
};

export default Header;
